import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { useAppDispatch, useAppSelector } from "@/shared/hooks";
import { getWorkerConflictKey } from "@/shared/utils";
import { selectTheme, selectShowImages } from "@/features/settings/selectors";
import {
  useApplyApplicationGetCompletenessQuery,
  useApplyApplicationSubmitMutation,
  useApplyApplicationValidateTokenQuery,
  type SectionKey,
  type CompletenessResponse,
} from "@/features/hr/hiring";
import type { SubmitApplicationError, SubmitApplicationResponse } from "@/features/hr/hiring/api/applyApplication";
import { setSocialCategoriesOpened } from "@/features/hr/hiring/slice";
import { SECTIONS, getDisabledSteps, getCompletedSteps } from "./applyPageLogic";

export interface UseApplyApplicationPageReturn {
  token: string | undefined;
  active: SectionKey;
  setActive: (section: SectionKey) => void;
  showConfirm: boolean;
  setShowConfirm: (show: boolean) => void;
  showPrompt: { type: "success" | "error" | null; text?: string };
  setShowPrompt: (prompt: { type: "success" | "error" | null; text?: string }) => void;
  isSubmitted: boolean;
  isChief: boolean;
  submitResponse: SubmitApplicationResponse | null;
  completeness: CompletenessResponse | null;
  completenessLoadError: string | null;
  socialCategoriesWasOpened: boolean;
  validationData: ReturnType<typeof useApplyApplicationValidateTokenQuery>["data"];
  validationLoading: boolean;
  validationError: boolean;
  submitApplication: ReturnType<typeof useApplyApplicationSubmitMutation>[0];
  submitIsLoading: boolean;
  disabledSteps: SectionKey[];
  completedSteps: SectionKey[];
  sections: typeof SECTIONS;
  handleSubmit: () => Promise<void>;
  t: ReturnType<typeof useTranslation>["t"];
}

export function useApplyApplicationPage(): UseApplyApplicationPageReturn {
  const { t } = useTranslation("ApplyApplicationPage");
  const { t: tCommon } = useTranslation("Common");
  const { token } = useParams<{ token: string }>();
  const dispatch = useAppDispatch();

  const [active, setActive] = useState<SectionKey>(SECTIONS[0].id);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showPrompt, setShowPrompt] = useState<{
    type: "success" | "error" | null;
    text?: string;
  }>({ type: null });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitResponse, setSubmitResponse] = useState<SubmitApplicationResponse | null>(null);

  const completeness = useAppSelector((state) => state.completeness.data);
  const completenessLoadError = useAppSelector((state) => state.completeness.error);
  const socialCategoriesWasOpened = useAppSelector((state) => state.completeness.socialCategoriesWasOpened);
  const theme = useAppSelector(selectTheme);
  const showImages = useAppSelector(selectShowImages);

  const {
    data: validationData,
    isLoading: validationLoading,
    isError: validationError,
  } = useApplyApplicationValidateTokenQuery(token!, { skip: !token });

  useApplyApplicationGetCompletenessQuery(token!, {
    skip: !token || !validationData?.valid,
  });

  const [submitApplication, { isLoading: submitIsLoading }] = useApplyApplicationSubmitMutation();

  const isChief = validationData?.application_type === "chief_onboarding";

  const disabledSteps = useMemo(() => getDisabledSteps(completeness), [completeness]);

  const completedSteps = useMemo(
    () => getCompletedSteps(completeness, token, socialCategoriesWasOpened),
    [completeness, token, socialCategoriesWasOpened],
  );

  useEffect(() => {
    if (!token) return;

    const storageKey = `social_categories_opened_${token}`;
    const wasOpened = localStorage.getItem(storageKey) === "true";

    if (active === "social_categories" && !wasOpened) {
      localStorage.setItem(storageKey, "true");
      if (completeness && !completeness.sections.social_categories) {
        dispatch(setSocialCategoriesOpened());
      }
    }
  }, [active, token, completeness, dispatch]);

  useEffect(() => {
    document.body.classList.add("surface-secondary-fill");

    if (theme === "dark") {
      document.body.classList.add("dark");
    }
    if (!showImages) {
      document.body.classList.add("no-images");
    }

    return () => {
      document.body.classList.remove("surface-secondary-fill");
    };
  }, [theme, showImages]);

  async function handleSubmit() {
    if (!token || submitIsLoading) return;
    try {
      const response = await submitApplication(token).unwrap();
      setShowConfirm(false);
      setSubmitResponse(response);
      setIsSubmitted(true);
    } catch (e: unknown) {
      setShowConfirm(false);
      const conflictKey = getWorkerConflictKey(e);
      setShowPrompt({ type: "error", text: conflictKey ? tCommon(conflictKey) : undefined });

      if (e && typeof e === "object" && "data" in e) {
        const errorData = e.data as SubmitApplicationError;
        if (errorData?.missing_sections && errorData.missing_sections.length > 0) {
          console.warn("Missing sections:", errorData.missing_sections);
        }
      }

      console.error("Submit application error", e);
    }
  }

  return {
    token,
    active,
    setActive,
    showConfirm,
    setShowConfirm,
    showPrompt,
    setShowPrompt,
    isSubmitted,
    isChief,
    submitResponse,
    completeness,
    completenessLoadError,
    socialCategoriesWasOpened,
    validationData,
    validationLoading,
    validationError,
    submitApplication,
    submitIsLoading,
    disabledSteps,
    completedSteps,
    sections: SECTIONS,
    handleSubmit,
    t,
  };
}
